/* eslint-disable */
/*
 * video-vitals.js — does this file actually contain WATCHABLE video and AUDIBLE audio?
 *
 * WHY THIS EXISTS
 * ---------------
 * /repair-video diagnosed the CONTAINER and called the answer the file's health. Measured
 * on real fixtures, a video whose second half is a black screen, one that is frozen, and
 * one whose audio is pure silence all produce ZERO demux errors and ZERO decode errors —
 * so all three scored "100 — Отлично — Проблемы не обнаружены". The tool then ran a
 * stream copy and reported "Степень восстановления: 100%".
 *
 * That happened 403 times in 30 days: no problem found, pure copy, 98.4 % claimed, ~99 s
 * of the user's time. It is the largest "success" bucket on the tool, and it is what a
 * person means by "не помогло" (comment #1529) and "совершенно ничего не изменило" (#829).
 *
 * The instrument was already on the page: FFmpeg-WASM ships blackdetect / freezedetect /
 * silencedetect, verified present in OUR build. Nothing new is downloaded.
 *
 * MEASUREMENTS THAT SHAPED THIS (real fixtures, through the real WASM build)
 * -------------------------------------------------------------------------
 * Sampling rate — fps=4 is not a guess:
 *   fps=4              correct on every fixture (clean 0 %, half-dead 50 %)
 *   fps=2              FALSE freezes on THREE healthy files — telling someone their video
 *                      is frozen when it is not is worse than saying nothing
 *   -skip_frame nokey  blind (missed a half-black file) AND false-positive on a clean one
 *   scale=160 first    SLOWER than full size (13.3 s vs 10.7 s): the cost is decoding, not
 *                      pixel count, so shrinking the frame buys nothing
 *
 * Coverage, not presence — a black leader and a black tail are ordinary:
 *   black leader (legit) 17 %   black tail (legit) 9 %   fade-out 2 %
 *   half the video dead  50 %   clean 8 s / 3 min  0 %
 *   => BLACK_DAMAGE_PCT = 30: 1.8x above the worst legitimate case, 1.7x below the defect.
 *
 * ⚠️⚠️ FREEZE IS NOT A DAMAGE SIGNAL. A legitimate static slide measured 100 % frozen — as
 * would any locked-off camera, screen recording or presentation. It is carried as an
 * observation with a timestamp and never raised as damage; making it a verdict would
 * invent a whole new class of wrong answers.
 *
 * ⚠️ Read the detectors from a FILE, never from the log. FFmpegProcessor trims its log on
 * purpose (~560 chars survived here), so on a long file the early detections are silently
 * dropped and coverage comes out too low. `metadata=mode=print:file=…` is complete
 * regardless of how many events there are.
 *
 * Cost: ~4 s per minute of video (decode-bound). Files longer than FULL_PASS_MAX_S are
 * sampled in three windows (head / middle / tail) instead — measured to return the SAME
 * verdict on a recording that dies partway (33 % either way) for a third less time, and
 * the saving grows with length. The tail window matters most: "the camera stopped" is the
 * shape real damage takes.
 *
 * THE DECODER'S OWN ACCOUNT (2026-09-29, comment #1784 — «ничего не исправляет»)
 * -----------------------------------------------------------------------------
 * The same pass also answers "did the picture DECODE, and how cleanly?" — at no extra cost,
 * because the frames are decoded anyway (measured: 14.8 s with the census vs 14.7 s without,
 * 30 s of 720p). Two numbers:
 *   decoded — frames the decoder actually produced. A first output with `-fps_mode
 *             passthrough` carries every decoded frame untouched, and the status line's
 *             `frame=` reports the FIRST video output. The vitals chain is the second output.
 *   errors  — lines the VIDEO DECODER printed at -v error, counted by a listener on the
 *             engine for the whole run. Not from the result of `_fs.exec`: that is the last
 *             ten lines, which capped every count the page ever made at ten.
 * Why the page needs them: black/freeze/silence are read against `time=`, and `time=` moves
 * with the AUDIO too. A video whose picture cannot be decoded at all (decoder settings
 * missing everywhere — 0 of 900 frames, 4947 decoder errors, measured) came out of this pass
 * as "0 % black, 0 % frozen", i.e. healthy. And the diagnosis read decoder errors from the
 * probe's first frames only, so bit rot in the middle of a clip (30 errors at 13–15 s) and
 * macroblock damage at the head ("error while decoding MB", which no pattern matched) were
 * both reported as "no problems found".
 *
 * A BUDGET IN WORK TIME, NOT IN MEDIA TIME (2026-10-13, comment #963)
 * ------------------------------------------------------------------
 * `-t 180` bounds the MEDIA a pass may read; what the person pays is frames x pixels over the
 * speed of the device. A 63 s 1080p60 phone clip is one "full" pass by that bound, and on an
 * eight-A53 phone it decoded at 2.8 frames a second: 22.5 minutes on the engine the repair was
 * waiting for (the same clip on a desktop: 82 s). Fleet, 30 days: p50 1 s, p90 32 s, p95 73 s,
 * maximum 35 minutes.
 * `opts.budgetMs` is the answer: each run is handed to the engine with a work-time limit, and
 * the core ends that run by itself — status lines, counts and detector files up to that moment
 * are all there, and the instance stays alive (measured on the shipped core, see _fs.exec).
 * When the first pass is cut, the rate it measured sizes what is left: a window in the middle
 * and one at the end, as long as that rate decodes in the remaining budget. The verdict then
 * covers what was looked at and says so (`mode: 'budget'`, `analysedSeconds`).
 * A cut window that produced no frame measured nothing — it is dropped, never read as "the
 * picture does not decode".
 */
(function (global) {
    'use strict';

    var FPS = 4;                  // see the table above — 2 fps invents freezes
    var FULL_PASS_MAX_S = 180;    // beyond this, sample windows instead of decoding it all
    var WINDOW_S = 40;
    var HEAD_SHARE = 0.6;         // of a budget: the first pass; the rest is split between later windows
    var MIN_WINDOW_S = 1;         // a sampled window shorter than this says nothing
    var SEEK_ROOM = 0.6;          // a window starts by decoding up to its first frame: leave room for it

    // Suggested thresholds, exported rather than applied here: a threshold belongs to the
    // caller that shows the verdict, and these numbers came from the fixtures listed above.
    var BLACK_DAMAGE_PCT = 30;
    var SILENCE_FLAG_PCT = 90;

    function parseMeta(txt, key, span) {
        var total = 0, open = null, spans = [];
        var lines = String(txt || '').split('\n');
        for (var i = 0; i < lines.length; i++) {
            var eq = lines[i].indexOf('=');
            if (eq < 0) continue;
            var k = lines[i].slice(0, eq).trim();
            var v = parseFloat(lines[i].slice(eq + 1));
            if (!isFinite(v)) continue;
            if (k === key + '_start') open = v;
            else if (k === key + '_end' && open !== null) {
                total += v - open; spans.push([open, v]); open = null;
            }
        }
        // An unterminated run reaches the end of what we analysed — which is exactly the
        // signature of a recording that died and never came back.
        if (open !== null) { total += Math.max(0, span - open); spans.push([open, span]); }
        return { seconds: total, spans: spans };
    }

    /*
     * How far this pass actually got, read from ffmpeg's own closing status line
     * ("frame=… time=00:00:10.00 …").
     *
     * ⚠️ NOT from the metadata file: `metadata=print` emits a line only for frames that
     * CARRY metadata, so on a file with one early detection the last timestamp there is the
     * last detection, not the end of the analysed span — a denominator that shrinks exactly
     * when something was found, which would inflate every percentage.
     *
     * The status line survives the processor's log trimming because it is the last thing
     * printed.
     */
    function processedSeconds(logs) {
        var s = String(logs || ''), best = 0, at = -1;
        while ((at = s.indexOf('time=', at + 1)) >= 0) {
            var m = /^(\d+):(\d\d):(\d\d(?:\.\d+)?)/.exec(s.slice(at + 5));
            if (!m) continue;
            var v = (+m[1]) * 3600 + (+m[2]) * 60 + parseFloat(m[3]);
            if (isFinite(v) && v > best) best = v;
        }
        return best;
    }

    /* Lines a VIDEO decoder prints are tagged with its name — the codec name the probe
     * reports, except AV1, which this engine decodes with libdav1d. */
    function decoderLine(codec) {
        var c = String(codec || '').toLowerCase().replace(/[^a-z0-9_]/g, '');
        if (!c) return null;
        var names = c === 'av1' ? ['av1', 'libdav1d'] : (c === 'h265' ? ['hevc'] : [c]);
        return new RegExp('^\\[(?:' + names.join('|') + ') @ ');
    }
    // "error while decoding MB 72 0, bytestream 52168" → "error while decoding MB N N, bytestream N"
    function kindOf(line) {
        return String(line).replace(/^\[[^\]]*\]\s*/, '').replace(/-?\d+/g, 'N').replace(/\s+/g, ' ').trim().slice(0, 60);
    }
    function lastFrameCount(s) {
        var m, re = /frame=\s*(\d+)/g, n = null;
        while ((m = re.exec(s)) !== null) n = +m[1];
        return n;
    }

    /*
     * Only OUR run's lines on a shared engine (2026-09-30). A 'log' listener hears the whole
     * engine, and /repair-video starts two passes on it at once — the census below and the deep
     * scan (`-c copy -f null`, which prints the first-frames chatter of its own stream probe and
     * a status line at the END of the file). Fixture C (120 decoder errors by native ffmpeg, all
     * in the first 2 s) came out "180 errors (0:00, 0:20)" in one stand run of three. A banner
     * cannot mark our start (the processor's boundary, ownRunLog): at -v error there is none.
     * But runs in the worker are strictly sequential — EXEC is synchronous in its onmessage — so
     * a cheap message posted in the SAME tick as our exec, with nothing able to queue between
     * them, answers exactly when every run queued before ours has finished, and the answer's
     * microtask runs before our first line arrives. From then on the log is ours.
     *
     *   var mine = VideoVitals.fence(engine);   // then, with NO await in between:
     *   await FFmpegProcessor._fs.exec(args);   // count a line only while mine() is true
     *
     * ⚠️ `_fs.exec` posts synchronously only while the engine exists: fence the engine that
     * `_getFFmpeg()` returned. On the multithreaded core (not used by /repair-video) pthread
     * prints are proxied and may arrive late — there the fence is the best available, not exact.
     */
    function fence(eng) {
        if (!eng || typeof eng.listDir !== 'function') return function () { return true; };
        var open = false;
        var opened = function () { open = true; };
        try { eng.listDir('/').then(opened, opened); } catch (_) { return function () { return true; }; }
        return function () { return open; };
    }

    /*
     * One exec with a listener on the engine for its whole duration. Returns the counts the
     * listener saw; `logs` is the processor's ten-line tail, kept as the fallback for a
     * processor without `_getFFmpeg` (then the counts are as good as the tail is). Lines of
     * runs queued ahead of ours are dropped (`fence`) and counted in `foreign`.
     */
    async function execCounting(fp, args, decRe, run) {
        run = run || {};
        // spans: WHERE the decoder complained, as [from, to, count] in seconds of the pass. The
        // engine prints its status line on a wall-clock period, so an error line can only be
        // placed BETWEEN the status line before it and the one after it — on a fast decode
        // that is several seconds of media, and pinning it to the earlier one put mid-file
        // damage at "0:00" (measured: 18 errors at 0:08 reported as 0:00). A range is the
        // honest answer; -stats_period 0.2 (below) keeps it narrow.
        var st = { code: null, logs: '', errors: 0, kinds: {}, frames: null, time: 0, lines: 0, noStream: false, spans: [], foreign: 0, ms: 0, cut: false };
        var clock = function () { return (global.performance && global.performance.now) ? global.performance.now() : Date.now(); };
        var t0 = clock();
        var lastT = 0, pendingN = 0, pendingFrom = 0;
        var closePending = function (to) {
            if (!pendingN) return;
            var prev = st.spans[st.spans.length - 1];
            if (prev && pendingFrom <= prev[1] + 0.001) { prev[1] = Math.max(prev[1], to); prev[2] += pendingN; }
            else st.spans.push([pendingFrom, Math.max(pendingFrom, to), pendingN]);
            pendingN = 0;
        };
        var eng = null;
        try { eng = fp._getFFmpeg ? fp._getFFmpeg() : null; } catch (_) { eng = null; }
        var mine = function () { return true; };
        var onLog = function (e) {
            var m = e && e.message;
            if (typeof m !== 'string') return;
            if (!mine()) { st.foreign++; return; }
            st.lines++;
            if (decRe && decRe.test(m)) {
                st.errors++;
                var k = kindOf(m);
                st.kinds[k] = (st.kinds[k] || 0) + 1;
                if (!pendingN) pendingFrom = lastT;
                pendingN++;
            }
            if (m.indexOf('matches no streams') >= 0) st.noStream = true;
            if (m.indexOf('frame=') >= 0) { var f = lastFrameCount(m); if (f !== null) st.frames = f; }
            if (m.indexOf('time=') >= 0) {
                var t = processedSeconds(m);
                if (t > st.time) st.time = t;
                if (t >= lastT) { closePending(t); lastT = t; }
                if (run.onStatus) { try { run.onStatus({ time: st.time, frames: st.frames || 0, errors: st.errors, wallMs: clock() - t0 }); } catch (_) {} }
            }
        };
        if (eng && eng.on) { try { eng.on('log', onLog); } catch (_) { eng = null; } }
        try {
            // ⚠️ No await between the fence and the exec: anything awaited here would let
            // another run slip in behind the fence.
            mine = fence(eng);
            t0 = clock();
            var res = await fp._fs.exec(args, run.timeoutMs > 0 ? run.timeoutMs : 0);
            st.code = res && res.code;
            st.logs = (res && res.logs) || '';
        } catch (e) { st.threw = true; /* a null-sink run ends via abort in this build */ }
        finally { if (eng && eng.off) { try { eng.off('log', onLog); } catch (_) {} } }
        st.ms = clock() - t0;
        // The core ends a run that reached its limit with code 1; a run that failed with the
        // same code did so long before the limit.
        st.cut = !!(run.timeoutMs > 0 && st.code === 1 && st.ms >= run.timeoutMs * 0.9);
        closePending(Math.max(lastT, st.time));
        if (!st.lines) {
            // No listener: the tail is all there is.
            st.time = processedSeconds(st.logs);
            st.frames = lastFrameCount(st.logs);
            if (decRe) {
                String(st.logs).split(' | ').forEach(function (l) {
                    if (decRe.test(l)) { st.errors++; var k = kindOf(l); st.kinds[k] = (st.kinds[k] || 0) + 1; }
                });
            }
        }
        return st;
    }

    async function readText(fs, name) {
        try {
            var b = await fs.read(name);
            var u8 = (b instanceof Uint8Array) ? b : new Uint8Array(b);
            return new TextDecoder().decode(u8);
        } catch (e) { return ''; }
    }
    async function quietDelete(fs, name) { try { await fs.del(name); } catch (e) {} }

    /*
     * inspect(file, opts) → {ok, mode, analysedSeconds, black, freeze, silence, video, ms}
     *   opts.duration   — total seconds (the caller already probed it; we do not re-probe)
     *   opts.processor  — FFmpegProcessor (defaults to the global)
     *   opts.hasAudio   — false skips the audio pass entirely
     *   opts.hasVideo   — false: an audio-only file, no picture to look at
     *   opts.videoCodec — the probe's codec name ('h264', 'hevc', 'av1' …): whose lines to count
     *   opts.fps        — the probe's frame rate, for `video.expected`
     *   opts.onProgress(fraction)
     *   opts.budgetMs   — work-time budget for the whole inspection (0 = none): see the header
     *   opts.onStatus({seg, segs, time, frames, errors, wallMs, limitMs, ss, len})
     *                   — every status line of the run in progress: how far, how fast
     *   opts.shouldStop() — asked before each further window: true ends the inspection with
     *                   what it has (the caller needs the engine)
     *
     * `video` (null when opts.hasVideo === false):
     *   decoded  — frames the decoder produced over the analysed span
     *   expected — fps × the span the VIDEO covered (0 when fps is unknown)
     *   errors   — video-decoder error lines over the same span
     *   kinds    — [[kind, count]…] most frequent first, digits folded ("… MB N N …")
 *   errSpans — [[from, to, count]…] seconds of the FILE where those errors were printed
     *   stalled  — true when a window could not start the video decoder at all
     *   windows  — [{ss, span, decoded, expected, errors}] per analysed window
     * `mode` — 'full' (the file, to its end or to the media bound), 'windows' (a long file,
     *   three samples), 'budget' (a run was cut by the work-time budget). `cut`, `stopped`.
     * `foreign` — log lines of other runs that were in the engine ahead of ours and were
     *   dropped (the page's deep scan starts together with this pass).
     * ⚠️ black/freeze describe the frames that DID decode. With `video.decoded === 0` they
     * are 0 % because there was nothing to look at, not because the picture is fine.
     */
    async function inspect(file, opts) {
        opts = opts || {};
        var fp = opts.processor || global.FFmpegProcessor;
        if (!fp || !fp._fs || typeof fp._fs.exec !== 'function') return null;
        var fs = fp._fs;
        var duration = opts.duration > 0 ? opts.duration : 0;
        var now = function () { return (global.performance && global.performance.now) ? global.performance.now() : Date.now(); };
        var started = now();
        var work = 'vitals_' + Date.now() + '.' + ((file.name || 'in.mp4').split('.').pop() || 'mp4');
        /* Mounted, not copied (2026-09-30). `_fs.write` materialises the WHOLE file in the
         * engine's heap to decode a few windows of it — a 1 GB recording is a 1 GB copy next to
         * the one the page already holds, and on a phone that is the tab. The census now also
         * runs on the repair's OUTPUT, so it would do it twice. WORKERFS reads on demand. */
        var mnt = null;
        if (typeof fp.mountInput === 'function') {
            try { mnt = await fp.mountInput(file, { name: work }); work = mnt.path; } catch (e) { mnt = null; }
        }
        if (!mnt) {
            try { await fs.write(work, file); } catch (e) { return null; }
        }

        var vf = 'fps=' + FPS
            + ',blackdetect=d=0.4:pix_th=0.10'
            + ',freezedetect=n=0.003:d=0.5'
            + ',metadata=mode=print:file=vv_v.txt';
        var af = 'silencedetect=n=-50dB:d=1,ametadata=mode=print:file=vv_a.txt';

        var black = { seconds: 0, spans: [] };
        var freeze = { seconds: 0, spans: [] };
        var silence = { seconds: 0, spans: [] };
        var analysed = 0, analysedVideo = 0;
        var mode = (!duration || duration <= FULL_PASS_MAX_S) ? 'full' : 'windows';
        var plan = [];
        // ⚠️ ALWAYS bounded, including the "full" pass. Shipping it unbounded cost users
        // minutes: measured on 209 real runs, 30 ran over 30 s, 12 over two minutes and one
        // took 805 s — because without -t ffmpeg decodes the entire file, and a file whose
        // duration the container does not state (webm, and every moov-missing recording)
        // takes this branch by definition. Complaint #1564 sat through 235 s of it.
        //
        // The bound is a CEILING, never a claim about the file: always FULL_PASS_MAX_S, not
        // the reported duration. A shorter file simply hits EOF first and the measured span
        // says so. Deriving the bound from `duration` would hand the analysis over to a
        // field that is routinely wrong — complaint #1564's 326 MB webm reported a duration
        // of 2 seconds, and a 2-second budget would have analysed nothing while looking
        // perfectly reasonable in the logs.
        var budget = opts.budgetMs > 0 ? opts.budgetMs : 0;
        if (mode === 'full') plan.push({ ss: null, len: FULL_PASS_MAX_S, limit: budget * (duration > 0 ? HEAD_SHARE : 1) });
        else {
            plan.push({ ss: 0, len: WINDOW_S, limit: budget / 3 });
            plan.push({ ss: Math.max(0, duration / 2 - WINDOW_S / 2), len: WINDOW_S, limit: budget / 3, where: 'middle' });
            plan.push({ ss: Math.max(0, duration - WINDOW_S), len: WINDOW_S, limit: budget / 3, where: 'tail' });
        }
        var cut = false, stopped = false, killed = false;

        var withVideo = opts.hasVideo !== false;
        var decRe = withVideo ? decoderLine(opts.videoCodec) : null;
        var fpsIn = opts.fps > 0 && opts.fps < 1000 ? opts.fps : 0;
        var video = withVideo ? { decoded: 0, expected: 0, errors: 0, kinds: {}, errSpans: [], stalled: false, windows: [] } : null;
        // Lines of OTHER runs that were in the engine when ours was queued (see execCounting).
        var foreign = 0;
        var seg = null, segIdx = 0;
        var counted = async function (a, re) {
            var mySeg = seg, myIdx = segIdx;
            var r = await execCounting(fp, a, re, {
                timeoutMs: mySeg && mySeg.limit > 0 ? mySeg.limit : 0,
                onStatus: opts.onStatus ? function (s) {
                    opts.onStatus({ seg: myIdx, segs: plan.length, time: s.time, frames: s.frames, errors: s.errors,
                                    wallMs: s.wallMs, limitMs: mySeg && mySeg.limit > 0 ? mySeg.limit : 0,
                                    ss: mySeg ? (mySeg.ss || 0) : 0, len: mySeg ? mySeg.len : 0 });
                } : null
            });
            foreign += r.foreign || 0;
            return r;
        };

        for (var i = 0; i < plan.length; i++) {
            if (i > 0 && opts.shouldStop) {
                var stopNow = false;
                try { stopNow = !!opts.shouldStop(); } catch (_) {}
                if (stopNow) { stopped = true; break; }
            }
            seg = plan[i]; segIdx = i;
            // -stats explicitly: at -v error ffmpeg suppresses its status line, and that
            // line is where the processed duration is read from. Measured: without it the
            // span fell back to the planned bound and an unterminated black run was scored
            // against 180 s instead of the real 10 — 97 % "black" on a half-black file.
            var head = ['-v', 'error', '-stats', '-stats_period', '0.2'];
            // -ss before -i: seeking on the input is what keeps a sampled window cheap.
            // The start is a whole second; the part it moved back by is added to the length, so
            // a tail window still ends where the file does.
            if (seg.ss !== null) {
                var whole = Math.floor(seg.ss);
                if (seg.len > 0) seg.len += seg.ss - whole;
                seg.ss = whole;
                head = head.concat(['-ss', String(seg.ss)]);
            }
            // -t on EVERY segment, the full pass included: the bound is what stops an
            // unknown-duration file from becoming an unbounded decode.
            if (seg.len > 0) head = head.concat(['-t', String(Math.round(seg.len * 100) / 100)]);
            head = head.concat(['-i', work]);
            var args;
            if (withVideo) {
                // Output 1 counts: every decoded frame, untouched. `0:V` is a video stream
                // that is not a cover picture. Output 2 is the vitals chain.
                args = head.concat(['-map', '0:V:0', '-fps_mode', 'passthrough', '-f', 'null', '-',
                                    '-map', '0:V:0', '-vf', vf]);
                args = args.concat(opts.hasAudio === false ? [] : ['-map', '0:a:0?', '-af', af]);
            } else {
                args = head.concat(['-vf', vf]).concat(opts.hasAudio === false ? ['-an'] : ['-af', af]);
            }
            args = args.concat(['-f', 'null', '-']);

            var run = await counted(args, decRe);
            if (withVideo && run.noStream && i === 0) {
                // No picture stream at all (the caller thought there was one): measure it as
                // the audio file it is, rather than report a video that "does not decode".
                withVideo = false; decRe = null; video = null;
                run = await counted(head.concat(['-vf', vf]).concat(opts.hasAudio === false ? ['-an'] : ['-af', af]).concat(['-f', 'null', '-']), null);
            }
            if (run.threw) killed = true;
            var vFrames = run.frames || 0;
            // The caller took the engine from under this run before it printed a frame (a press
            // on Repair in the first moments of the census): nothing was measured. Read as a
            // failed decode it made a healthy clip «the picture does not decode», and its
            // stream copy «fixed: picture» (14.10.2026). The sound retry below must not run
            // either — it would start on the instance the caller has just raised for its own work.
            if (vFrames === 0 && opts.shouldStop) {
                var taken = false;
                try { taken = !!opts.shouldStop(); } catch (_) {}
                if (taken) { stopped = true; break; }
            }
            if (run.cut) {
                cut = true;
                mode = 'budget';
                // What is left of the plan is re-sized by the rate this run measured: media
                // seconds decoded per second of work. Only the run itself can know it.
                var rate = run.ms > 0 && run.time > 0 ? run.time / (run.ms / 1000) : 0;
                var spent = now() - started;
                var left = Math.max(0, budget - spent);
                if (i === 0 && plan.length === 1 && duration > 0 && rate > 0 && left > 0) {
                    var covered = run.time;                       // the head is [0, covered]
                    var each = left / 2;
                    var winLen = Math.min(WINDOW_S, rate * (each / 1000) * SEEK_ROOM);
                    var rest = duration - covered;
                    if (rest > 0.5 && rest <= rate * (left / 1000) * SEEK_ROOM) {
                        // What is left of the file fits what is left of the budget: finish it.
                        plan.push({ ss: covered, len: rest + 1, limit: left, where: 'rest' });
                    } else if (winLen >= MIN_WINDOW_S && rest > winLen) {
                        var tailAt = Math.max(covered, duration - winLen);
                        var midAt = Math.max(covered, duration / 2 - winLen / 2);
                        var both = midAt + winLen <= tailAt;
                        if (both) plan.push({ ss: midAt, len: winLen, limit: each, where: 'middle' });
                        plan.push({ ss: tailAt, len: winLen, limit: both ? each : left, where: 'tail' });
                    }
                } else if (plan.length > 1 && rate > 0) {
                    for (var pj = i + 1; pj < plan.length; pj++) {
                        var fit = Math.min(plan[pj].len, rate * ((plan[pj].limit || 0) / 1000) * SEEK_ROOM);
                        if (!(fit >= MIN_WINDOW_S)) { plan.splice(pj, 1); pj--; continue; }
                        if (plan[pj].where === 'tail') plan[pj].ss = Math.max(0, duration - fit);
                        else if (plan[pj].where === 'middle') plan[pj].ss = Math.max(0, duration / 2 - fit / 2);
                        plan[pj].len = fit;
                    }
                }
                // Cut before its first frame (a later window spends its start decoding up to
                // the place it was asked for): nothing was measured — not "does not decode".
                if (withVideo && vFrames === 0) {
                    await quietDelete(fs, 'vv_v.txt');
                    await quietDelete(fs, 'vv_a.txt');
                    if (opts.onProgress) { try { opts.onProgress((i + 1) / plan.length); } catch (_) {} }
                    continue;
                }
            }
            var vFailed = withVideo && vFrames === 0 && (run.errors > 0 || run.code !== 0);
            // A video decoder that cannot start at all fails the whole run in milliseconds
            // ("Cannot determine format of input stream 0:0 after EOF"), audio included.
            // The sound is still worth measuring — "the picture is gone, the sound is fine"
            // is the most useful thing this page can say about such a file.
            if (withVideo && vFrames === 0) {
                await quietDelete(fs, 'vv_v.txt');
                if (opts.hasAudio !== false) {
                    var aRun = await counted(head.concat(['-map', '0:a:0?', '-af', af, '-f', 'null', '-']), null);
                    if (aRun.time > run.time) run.time = aRun.time;
                }
            }
            // A window with media in it (sound played, or the decoder complained) and no
            // picture: the video is there and does not decode. An empty window past the real
            // end of the data is not that.
            if (vFailed || (withVideo && vFrames === 0 && run.time > 0)) video.stalled = true;

            var vTxt = await readText(fs, 'vv_v.txt');
            var aTxt = await readText(fs, 'vv_a.txt');
            /*
             * The analysed span is MEASURED from the frame timestamps ffmpeg actually
             * emitted, not taken from the container's duration. Trusting `duration` put a
             * wrong denominator under every percentage: 128 of 209 real runs reported
             * analysing ≤3 s of files that took tens of seconds to decode, because webm and
             * moov-missing recordings often state no duration at all. With a denominator of
             * 2 s, one second of black reads as 50 % and this pass would have called a
             * healthy recording dead.
             */
            var spanLen = run.time;
            if (!(spanLen > 0)) {
                // No frames reported at all (a still-image track, or nothing decoded) —
                // fall back to what we asked for, never to the whole file.
                spanLen = seg.ss === null ? (seg.len || 0)
                    : Math.min(seg.len, Math.max(0, (duration || seg.len + seg.ss) - seg.ss));
            }
            await quietDelete(fs, 'vv_v.txt');
            await quietDelete(fs, 'vv_a.txt');

            var b = parseMeta(vTxt, 'lavfi.black', spanLen);
            var f = parseMeta(vTxt, 'lavfi.freezedetect.freeze', spanLen);
            var s = parseMeta(aTxt, 'lavfi.silence', spanLen);
            black.seconds += b.seconds;
            freeze.seconds += f.seconds;
            silence.seconds += s.seconds;
            // Offsets inside a window are relative to the window; shift them so the caller
            // can show a timestamp that matches the user's own player.
            var shift = seg.ss || 0;
            for (var j = 0; j < b.spans.length; j++) black.spans.push([b.spans[j][0] + shift, b.spans[j][1] + shift]);
            for (var k = 0; k < f.spans.length; k++) freeze.spans.push([f.spans[k][0] + shift, f.spans[k][1] + shift]);
            for (var m = 0; m < s.spans.length; m++) silence.spans.push([s.spans[m][0] + shift, s.spans[m][1] + shift]);
            analysed += spanLen;
            if (withVideo) {
                // Expected frames only over MEASURED media time — never over the fallback
                // bound, which would invent frames for a window past the end of the data.
                var winExpected = fpsIn && run.time > 0 ? Math.round(fpsIn * run.time) : 0;
                video.decoded += vFrames;
                video.errors += run.errors;
                video.expected += winExpected;
                for (var kk in run.kinds) video.kinds[kk] = (video.kinds[kk] || 0) + run.kinds[kk];
                for (var es = 0; es < run.spans.length; es++) {
                    video.errSpans.push([run.spans[es][0] + shift, run.spans[es][1] + shift, run.spans[es][2]]);
                }
                video.windows.push({ ss: shift, span: Math.round(run.time * 10) / 10, decoded: vFrames,
                                     expected: winExpected, errors: run.errors });
                if (vFrames > 0) analysedVideo += spanLen;
            }

            if (opts.onProgress) { try { opts.onProgress((i + 1) / plan.length); } catch (_) {} }
        }

        // The caller took the engine while a run was in progress (it does not come back to ask
        // between windows then): what was counted up to that moment is real, the rest of the
        // file was not looked at — say so.
        if (!stopped && killed && opts.shouldStop) { try { stopped = !!opts.shouldStop(); } catch (_) {} }

        if (mnt) { try { await mnt.dispose(); } catch (_) {} } else await quietDelete(fs, work);

        var pct = function (sec, over) { return over > 0 ? Math.round(100 * sec / over) : 0; };
        // Picture coverage is measured against the time the picture was actually SEEN.
        var picSpan = withVideo ? analysedVideo : analysed;
        black.pct = pct(black.seconds, picSpan);
        freeze.pct = pct(freeze.seconds, picSpan);
        silence.pct = pct(silence.seconds, analysed);
        if (video) {
            video.kinds = Object.keys(video.kinds).map(function (k) { return [k, video.kinds[k]]; })
                .sort(function (a, b) { return b[1] - a[1]; }).slice(0, 5);
        }
        return {
            ok: true,
            mode: mode,
            cut: cut,
            stopped: stopped,
            analysedSeconds: Math.round(analysed),
            black: black,
            freeze: freeze,
            silence: silence,
            video: video,
            foreign: foreign,
            ms: Math.round(now() - started),
        };
    }

    global.VideoVitals = {
        inspect: inspect,
        fence: fence,
        BLACK_DAMAGE_PCT: BLACK_DAMAGE_PCT,
        SILENCE_FLAG_PCT: SILENCE_FLAG_PCT,
        FULL_PASS_MAX_S: FULL_PASS_MAX_S,
        _parseMeta: parseMeta,
        _decoderLine: decoderLine,
        _kindOf: kindOf,
    };
})(typeof window !== 'undefined' ? window : this);
